import type { ProductOptionDTO } from "../api/client";
import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";

function nameLo(row: ProductOptionDTO): string {
  return (row.name as { lo?: string })?.lo ?? "—";
}

function nameTh(row: ProductOptionDTO): string {
  return (row.name as { th?: string })?.th ?? "—";
}

function nameEn(row: ProductOptionDTO): string {
  return (row.name as { en?: string })?.en ?? "—";
}

type OptionsTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: ProductOptionDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onEdit: (row: ProductOptionDTO) => void;
  onDelete: (id: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function OptionsTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onEdit,
  onDelete,
  onPaginationChange,
  onSortingChange,
}: OptionsTableProps) {
  const columns: TanstackReactTable.ColumnDef<ProductOptionDTO>[] = [
    {
      id: "nameLo",
      header: "Option Name (LO/TH/EN)",
      cell: ({ row }) => {
        const option = row.original;
        const lo = nameLo(option);
        const th = nameTh(option);
        const en = nameEn(option);
        return (
          <div className="flex flex-wrap items-center gap-1">
            <span className="font-medium">{lo}</span>
            {th !== "—" && (
              <>
                <span className="text-muted-foreground">/</span>
                <span className="text-muted-foreground text-sm">{th}</span>
              </>
            )}
            {en !== "—" && (
              <>
                <span className="text-muted-foreground">/</span>
                <span className="text-muted-foreground text-sm">{en}</span>
              </>
            )}
          </div>
        );
      },
    },
    {
      id: "slug",
      header: "Slug",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.slug}
        </span>
      ),
    },
    {
      id: "values",
      header: "Values",
      cell: ({ row }) => {
        const count =
          row.original.valueCount ??
          (Array.isArray(row.original.values) ? row.original.values.length : 0);
        if (!count) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="text-sm text-muted-foreground">{count} values</span>
        );
      },
    },
    {
      id: "sortOrder",
      header: "ລຳດັບ",
      cell: ({ row }) => row.original.sortOrder,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        if (!canManage) return null;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: () => onDelete(row.original.id),
            variant: "destructive" as const,
          },
        ];
        return (
          <div data-tourid="catalog-options-actions">
            <RowActions actions={actions} maxInline={2} />
          </div>
        );
      },
    },
  ];

  return (
    <div data-tourid="catalog-options-table">
      <DataTable<ProductOptionDTO, unknown>
        noDataMessage="ບໍ່ພົບ Option"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(s) => {
          if (s[0]?.id === "") return;
          onSortingChange(s[0]?.id || "", s[0]?.desc || false);
        }}
      />
    </div>
  );
}
